import { lazy, Suspense, type ReactNode } from 'react';
import { Routes, Route } from 'react-router';
import LoginPage from './pages/auth/LoginPage';
import MfaPage from './pages/auth/MfaPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
// Phase 45.1b — public landing page for the IdP → API → dashboard SSO
// redirect chain. Eager import (like the other auth pages) so the
// one-time exchange code isn't burning its 60s TTL on a chunk download.
import SsoCallbackPage from './pages/auth/SsoCallbackPage';
import PlatformGuard from './components/auth/PlatformGuard';
import { SIGNUP_ENABLED } from './lib/env';
const PlatformDashboard = lazy(() => import('./pages/platform/PlatformDashboard'));
const OrgsList = lazy(() => import('./pages/platform/OrgsList'));
const OrgDetail = lazy(() => import('./pages/platform/OrgDetail'));
const CreateOrgPage = lazy(() => import('./pages/platform/CreateOrgPage'));
// Phase 45.4d — platform-admin org-group (franchise hierarchy) admin.
const OrgGroupsPage = lazy(() => import('./pages/platform/OrgGroupsPage'));
const PlatformPilotsPage = lazy(() => import('./pages/platform/PilotsPage'));
const PlatformContributionPage = lazy(() => import('./pages/platform/ContributionPage'));
const PlatformTextingPage = lazy(() => import('./pages/platform/TextingPage'));
const PilotPage = lazy(() => import('./pages/pilot/PilotPage'));
import ProtectedRoute from './components/auth/ProtectedRoute';
import { Link, Navigate } from 'react-router';
import { usePermission, useHasRole, useAnyPermission } from './hooks/usePermission';
import {
  useFeatureFlagStore,
  isFeatureEnabled,
} from '@commerce-os/pos-core/features/featureFlagsStore';
import { FeatureUnavailableNotice } from './components/common/FeatureUnavailableNotice';

// Phase 36.4 — `/register` access policy.
//
// Two independent paths can land a user on the register screen:
//   1. `/register?token=…` — magic-link sign-up from the operator-mint
//      flow (reduced 36.2). Always allowed; AuthService.register
//      validates + atomically redeems the token before creating the
//      org/user.
//   2. `/register` (no token) — open self-serve sign-up. Gated by
//      `VITE_ENABLE_SELF_SIGNUP=true` until 36b lights up the Stripe
//      Checkout self-serve CTA on the marketing site.
//
// `RegisterRoute` below resolves the policy at render time so a deep
// link with a token survives even when the env flag is off. The flag
// itself is read once, in `lib/env.ts`, shared with the login page.

function RegisterRouteGate() {
  // useSearchParams imported lazily inside the component so the route
  // file doesn't need to know about react-router internals beyond what
  // `Navigate` already requires.
  const params = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : '',
  );
  const hasToken = (params.get('token') ?? '').trim().length > 0;
  if (hasToken || SIGNUP_ENABLED) {
    return <RegisterPage />;
  }
  return <Navigate to="/login" replace />;
}
const AppShell = lazy(() => import('./components/layout/AppShell'));
// Phase 49 — hosted OAuth consent screen (the published
// `authorization_endpoint`). Lazy: third-party authorization is rare
// relative to everything else in the bundle.
const OAuthConsentPage = lazy(() => import('./pages/developer/OAuthConsentPage'));
const CommandCenter = lazy(() => import('./pages/home/CommandCenter'));
const OrgSettings = lazy(() => import('./pages/settings/OrgSettings'));
const LocationList = lazy(() => import('./pages/settings/LocationList'));
const UserManager = lazy(() => import('./pages/settings/UserManager'));
const RoleManager = lazy(() => import('./pages/settings/RoleManager'));
// Phase 33.5b — employee self-service portal: manager approval queues
// + employee-facing time-off / swap / availability pages.
const TimeOffApprovalQueue = lazy(() => import('./pages/roster/TimeOffApprovalQueue'));
const ShiftSwapApprovalQueue = lazy(() => import('./pages/roster/ShiftSwapApprovalQueue'));
// Phase 48.2 — manager queue for ESS open-shift claims.
const OpenShiftClaimsQueue = lazy(() => import('./pages/roster/OpenShiftClaimsQueue'));
const MyRequests = lazy(() => import('./pages/roster/MyRequests'));
const TimesheetsPage = lazy(() => import('./pages/roster/TimesheetsPage'));
// Phase 48.1 — real schedules surface: week-grid builder with labor-cost
// overlay (replaces the 2026-07-08 placeholder redirect).
const SchedulesPage = lazy(() => import('./pages/roster/schedules/SchedulesPage'));
// Phase 33.5c — accounting integrations connection-status dashboard.
const IntegrationsPage = lazy(() => import('./pages/settings/IntegrationsPage'));
// Phase 33.5d-1 — OAuth callback handler that completes the handshake.
const IntegrationOAuthCallback = lazy(() => import('./pages/settings/IntegrationOAuthCallback'));
const DataImportPage = lazy(() => import('./pages/settings/DataImportPage'));
// Roster Management suite — consolidates user + role management with
// timesheets + schedules + external scheduling integrations.
// UserManager + RoleManager are MOVED out of Settings into Roster;
// Settings > Users + Settings > Roles entries redirect to the new
// /roster paths so existing bookmarks keep working.
const DeviceList = lazy(() => import('./pages/settings/DeviceList'));
// Phase 66.14 — your own email choices (N5).
const NotificationSettings = lazy(() => import('./pages/settings/NotificationSettings'));
// Phase 68.4 — a business registering to text its guests.
const TextingSettingsPage = lazy(() => import('./pages/settings/texting/TextingSettingsPage'));
const DeviceDetail = lazy(() => import('./pages/settings/DeviceDetail'));
// Phase 62 follow-up — station admin (the setup checklist's KDS step).
const KdsStationsPage = lazy(() => import('./pages/settings/kds-stations/KdsStationsPage'));
const DiscountPresets = lazy(() => import('./pages/settings/DiscountPresets'));
// Phase 37.3 — Menu upsells authoring page. Consumes the 37.2 backend's
// /pos/upsells/* CRUD surface; gated on `pos.manage_upsells` (seeded in 37.1).
const UpsellsPage = lazy(() => import('./pages/settings/upsells/UpsellsPage'));
// Phase 52.5 — time-based price rules authoring.
const PriceRulesPage = lazy(() => import('./pages/settings/price-rules/PriceRulesPage'));
const MenusPage = lazy(() => import('./pages/settings/menus/MenusPage'));
// Phase 64 — the online menu (mirror the POS, diverge where customised).
const OnlineMenuPage = lazy(() => import('./pages/settings/online-menu/OnlineMenuPage'));
// 2026-09-29 — the merchant's own DoorDash Drive / Uber Direct accounts.
const DeliveryCouriersPage = lazy(() => import('./pages/settings/DeliveryCouriersPage'));
// Phase 65.2 — carrier shipping on the merchant's own EasyPost account.
const ShippingSettingsPage = lazy(() => import('./pages/settings/shipping/ShippingSettingsPage'));
const SystemHealthPage = lazy(() => import('./pages/settings/SystemHealthPage'));
// Phase 45.1b — enterprise SSO configuration (SAML/OIDC + JIT
// provisioning). Gated on `auth.configure_sso` (owner + it_admin).
const SsoSettingsPage = lazy(() => import('./pages/settings/SsoSettingsPage'));
const PumpsPage = lazy(() => import('./pages/settings/PumpsPage'));
const TurnTimesSettings = lazy(() => import('./pages/settings/TurnTimesSettings'));
const ReservationSettings = lazy(() => import('./pages/settings/ReservationSettings'));
const ReservationsPage = lazy(() => import('./pages/reservations/ReservationsPage'));
const ReservationDetail = lazy(() => import('./pages/reservations/ReservationDetail'));
// Phase 28.9b — unified host-stand surface (Reservations + Waitlist +
// History). Gated on pos.host_stand permission seeded by 28.9a
// migration 0045.
const HostStand = lazy(() => import('./pages/host-stand/HostStand'));
const SectionTemplates = lazy(() => import('./pages/settings/SectionTemplates'));
const RosterPage = lazy(() => import('./pages/settings/RosterPage'));
const VerticalPicker = lazy(() => import('./pages/settings/VerticalPicker'));
const FeatureFlagsPage = lazy(() => import('./pages/settings/FeatureFlagsPage'));
const BrandingPage = lazy(() => import('./pages/settings/BrandingPage'));
const StripeConnectPage = lazy(() => import('./pages/settings/payments/StripeConnectPage'));
const StripeConnectCallbackPage = lazy(() => import('./pages/settings/payments/StripeConnectCallbackPage'));
const ProcessingStatementPage = lazy(() => import('./pages/settings/payments/ProcessingStatementPage'));
const DualPricingPage = lazy(() => import('./pages/settings/payments/DualPricingPage'));
const StorefrontConfigPage = lazy(() => import('./pages/settings/StorefrontConfig'));
const QrCodesPage = lazy(() => import('./pages/settings/QrCodesPage'));
const MessagingTemplates = lazy(() => import('./pages/settings/messaging/Templates'));
const CustomFieldsPage = lazy(() => import('./pages/settings/custom-fields/CustomFieldsPage'));
// Phase 49 — self-service developer platform (OAuth apps + API keys).
// This is the URL published as `agent_auth.register_uri` in the OAuth
// discovery metadata, so the path is part of a public contract.
const DeveloperAppsPage = lazy(() => import('./pages/settings/DeveloperAppsPage'));
const InstalledAppsPage = lazy(() => import('./pages/settings/InstalledAppsPage'));
const AccountingPage = lazy(() => import('./pages/settings/AccountingPage'));
const LedgerPage = lazy(() => import('./pages/accounting/LedgerPage'));
const LoyaltyPrograms = lazy(() => import('./pages/settings/LoyaltyPrograms'));
// Phase 36.5.3b — tip pool admin (Operations hub). Tip-pool CRUD, rules
// editor across 5 distribution methods (percentage / hours_weighted /
// sales_percentage / points / hybrid), live preview, and a manual
// "Calculate now" trigger. Gated on `pos.configure_tip_pools` for write
// access; reads via `pos.view_tip_reports`.
const TipPools = lazy(() => import('./pages/settings/TipPools'));
// Phase 36 reduced 36.2 — operator-mint sign-up tokens for sales-driven
// onboarding. Gated on `billing.manage_signup_tokens` (seeded onto
// owner only by migration 0070; platform_admin via the wildcard branch).
const BillingPage = lazy(() => import('./pages/settings/BillingPage'));
// Phase 36.2c — `/billing/portal` deep-link target used by billing emails
// (payment failed / grace period / trial ending). Mints a fresh Stripe
// Customer Portal session on mount + redirects; see the page's docblock.
const BillingPortalRedirect = lazy(
  () => import('./pages/settings/billing/BillingPortalRedirect'),
);
// Phase 34 Session 34.4b — audit log + usage analytics dashboard pages.
const AuditLogPage = lazy(() => import('./pages/audit/AuditLogPage'));
const UsageAnalyticsPage = lazy(() => import('./pages/audit/UsageAnalyticsPage'));
// Phase 35 Session 35.3a — dashboard help center route (three-pane shell
// with category rail / article list / article reader). Search bar +
// thumbs feedback + File-a-ticket CTA land in 35.3b; tooltip launch
// set across ~40 dashboard fields lands in 35.3c.
const HelpCenter = lazy(() => import('./pages/help/HelpCenter'));
// Phase 35 Session 35.5c-2 — merchant Support Inbox panel. List +
// detail surfaces consume the 35.5b /support/tickets endpoints via
// the api-client; gated on `help.read` for visibility (the FileTicket
// modal itself gates writes on `help.submit_ticket`).
const TicketsListPage = lazy(() => import('./pages/help/tickets/TicketsListPage'));
const TicketDetailPage = lazy(() => import('./pages/help/tickets/TicketDetailPage'));
// Phase 35 Session 35.5c-3 — platform-admin Support Inbox queue.
// Consumes the /admin/support/tickets surface gated on
// `help.manage_inbox` (seeded only to platform_admin). Mounted under
// `/admin/help/support-inbox` so the URL prefix mirrors the email
// deep-link the 35.5c-1 SLA-breach template emits to ops teams.
const SupportInboxPage = lazy(() => import('./pages/help/admin/SupportInboxPage'));
const SupportTicketDetailPage = lazy(() => import('./pages/help/admin/SupportTicketDetailPage'));
// Phase 35 Session 35.7-5 — merchant help-analytics page consuming the
// 35.7-2a backend endpoints (overview + zero-result-searches + least-
// helpful-articles + GDPR wipe). Surfaces top articles, content gaps,
// and unhelpful-rate flags so merchant content authors can spot what's
// working and what isn't. Gated on `auth.view_usage_analytics` (same
// back-office reporting tier the 34.4b usage-analytics page uses).
const HelpAnalyticsPage = lazy(() => import('./pages/help/HelpAnalyticsPage'));
// Phase 35 Session 35.7-6 — platform-admin help-analytics page consuming
// the 35.7-2b cross-tenant endpoints (overview + engagement-over-time
// + ticket-deflection metric). Spans every merchant on the platform
// for content-team triage. Gated on `help.manage_inbox` (same authority
// surface the 35.5c-3 support-admin inbox uses).
const PlatformHelpAnalyticsPage = lazy(() => import('./pages/help/PlatformHelpAnalyticsPage'));
const AutomationList = lazy(() => import('./pages/automation/AutomationList'));
const AutomationDetail = lazy(() => import('./pages/automation/AutomationDetail'));
const ProductList = lazy(() => import('./pages/products/ProductList'));
const ProductForm = lazy(() => import('./pages/products/ProductForm'));
const CategoryManager = lazy(() => import('./pages/products/CategoryManager'));
// Phase 45.3e — menu versioning + scheduled publish admin surface.
// Gated on `pos.manage_products` (same authority as the catalog itself —
// a version IS catalog content), matching the API controllers.
const MenuVersionsPage = lazy(() => import('./pages/products/menu-versions/MenuVersionsPage'));
// Phase 45.3e — location groups (Settings → Organization). Gated on
// `auth.manage_org` (org structure, owner-tier), matching the API.
const LocationGroupsPage = lazy(
  () => import('./pages/settings/organization/LocationGroupsPage'),
);
const CustomerList = lazy(() => import('./pages/customers/CustomerList'));
const CustomerDetail = lazy(() => import('./pages/customers/CustomerDetail'));
const OrderList = lazy(() => import('./pages/orders/OrderList'));
const OrderDetail = lazy(() => import('./pages/orders/OrderDetail'));
const OfflineQueueList = lazy(() => import('./pages/offline-queue/OfflineQueueList'));
// Phase 51.4 — dispute (chargeback) management over the webhook ledger.
const DisputesPage = lazy(() => import('./pages/disputes/DisputesPage'));
const ActiveDeliveriesPage = lazy(() => import('./pages/active-deliveries/ActiveDeliveriesPage'));
const GiftCardList = lazy(() => import('./pages/gift-cards/GiftCardList'));
const GiftCardDetail = lazy(() => import('./pages/gift-cards/GiftCardDetail'));
// GiftCardReconciliation is now integrated into the OfflineQueueList page
const FloorPlanEditorPage = lazy(() => import('./pages/floor-plan/FloorPlanEditor'));
const TableSessions = lazy(() => import('./pages/floor-plan/TableSessions'));
const KdsMonitor = lazy(() => import('./pages/kds-monitor/KdsMonitor'));
const TaxDashboard = lazy(() => import('./pages/tax/TaxDashboard'));
const RegisterSessionList = lazy(() => import('./pages/registers/RegisterSessionList'));
const RegisterDetail = lazy(() => import('./pages/registers/RegisterDetail'));
const BarTabAdminList = lazy(() => import('./pages/bar-tabs/BarTabAdminList'));
const QuoteList = lazy(() => import('./pages/invoices/QuoteList'));
const QuoteForm = lazy(() => import('./pages/invoices/QuoteForm'));
const InvoiceList = lazy(() => import('./pages/invoices/InvoiceList'));
const InvoiceForm = lazy(() => import('./pages/invoices/InvoiceForm'));
const HouseAccountsPage = lazy(() => import('./pages/invoices/HouseAccountsPage'));
// Phase 50 — Field Service suite.
const FieldSettingsPage = lazy(() => import('./pages/field/FieldSettingsPage'));
// Phase 50.2 — Jobs list + detail.
const JobsList = lazy(() => import('./pages/field/JobsList'));
const JobDetail = lazy(() => import('./pages/field/JobDetail'));
// Phase 50.3 — Visits calendar + dispatch board.
const VisitsCalendar = lazy(() => import('./pages/field/VisitsCalendar'));
const DispatchBoard = lazy(() => import('./pages/field/DispatchBoard'));
const MediaLibraryPage = lazy(() => import('./pages/field/MediaLibraryPage'));
// Phase 50.6 — recurring service agreements.
const AgreementsPage = lazy(() => import('./pages/field/AgreementsPage'));
// Phase 50.7 — costing administration + monthly P&L / performance reports.
const MyLaborPage = lazy(() => import('./pages/field/MyLaborPage'));
const CostingPage = lazy(() => import('./pages/field/CostingPage'));
const FsReportsPage = lazy(() => import('./pages/field/FsReportsPage'));
// Phase 50.8 — CRM kanban + internal alerts inbox.
const CrmPage = lazy(() => import('./pages/field/CrmPage'));
const AlertsPage = lazy(() => import('./pages/field/AlertsPage'));
const InventoryOverview = lazy(() => import('./pages/inventory/InventoryOverview'));
const StockLevelList = lazy(() => import('./pages/inventory/StockLevelList'));
const StockItemDetail = lazy(() => import('./pages/inventory/StockItemDetail'));
const VendorList = lazy(() => import('./pages/inventory/VendorList'));
const PurchaseOrderList = lazy(() => import('./pages/inventory/PurchaseOrderList'));
const PurchaseOrderForm = lazy(() => import('./pages/inventory/PurchaseOrderForm'));
const PurchaseOrderDetail = lazy(() => import('./pages/inventory/PurchaseOrderDetail'));
const AdjustmentPage = lazy(() => import('./pages/inventory/AdjustmentPage'));
const CountSessionList = lazy(() => import('./pages/inventory/CountSessionList'));
const TransferList = lazy(() => import('./pages/inventory/TransferList'));
const InvoiceScanList = lazy(() => import('./pages/inventory/InvoiceScanList'));
const InvoiceScanReview = lazy(() => import('./pages/inventory/InvoiceScanReview'));
const TransferDetail = lazy(() => import('./pages/inventory/TransferDetail'));
const CountSheetPage = lazy(() => import('./pages/inventory/CountSheetPage'));
const WasteReport = lazy(() => import('./pages/inventory/WasteReport'));
const InventoryValuation = lazy(() => import('./pages/inventory/InventoryValuation'));
const IngredientForecast = lazy(() => import('./pages/inventory/IngredientForecast'));
const RecipeList = lazy(() => import('./pages/inventory/RecipeList'));
const RecipeForm = lazy(() => import('./pages/inventory/RecipeForm'));
const MenuEngineering = lazy(() => import('./pages/inventory/MenuEngineering'));
const FoodCostDashboard = lazy(() => import('./pages/inventory/FoodCostDashboard'));
// Settings reorg 2026-05-08 — landing-page hubs for the 5 consolidated
// SuiteNav tabs. Each hub renders a section-card grid pointing at the
// existing leaf URLs (which stay registered below for bookmark
// continuity). Hub cards filter through shared permission/feature metadata.
// Leaf pages enforce their own permission + feature checks server-side.
// Hub cards share that metadata
// with navigation; direct URL access is still enforced by page/API guards.
const WorkspaceHub = lazy(() => import('./pages/WorkspaceHub'));
const OrganizationHub = lazy(() => import('./pages/settings/OrganizationHub'));
const OperationsHub = lazy(() => import('./pages/settings/OperationsHub'));
const HardwareHub = lazy(() => import('./pages/settings/HardwareHub'));
const IntegrationsHub = lazy(() => import('./pages/settings/IntegrationsHub'));
const AdminHub = lazy(() => import('./pages/settings/AdminHub'));
// Inventory reorg 2026-05-08 — Procurement hub (Vendors + POs) groups
// two leaf surfaces under one SuiteNav tab. Same pattern as the
// Settings hubs.
const ProcurementHub = lazy(() => import('./pages/inventory/ProcurementHub'));
const CampaignList = lazy(() => import('./pages/marketing/CampaignList'));
const CampaignForm = lazy(() => import('./pages/marketing/CampaignForm'));
const CampaignRecipients = lazy(() => import('./pages/marketing/CampaignRecipients'));
const SegmentList = lazy(() => import('./pages/marketing/SegmentList'));
const SegmentForm = lazy(() => import('./pages/marketing/SegmentForm'));
const MarketingAnalytics = lazy(() => import('./pages/marketing/MarketingAnalytics'));
const ForecastPage = lazy(() => import('./pages/analytics/ForecastPage'));
const LaborForecastPage = lazy(() => import('./pages/analytics/LaborForecastPage'));
const BenchmarksPage = lazy(() => import('./pages/analytics/BenchmarksPage'));
const AssistantPage = lazy(() => import('./pages/analytics/AssistantPage'));
const ReportSelector = lazy(() => import('./pages/reports/ReportSelector'));
const DailySalesReport = lazy(() => import('./pages/reports/DailySalesReport'));
const EODReconciliationReport = lazy(() => import('./pages/reports/EODReconciliationReport'));
// Phase 66.11 — drawer counts over a range (V4) and online orders by week (V5).
const RegisterClosesReport = lazy(() => import('./pages/reports/RegisterClosesReport'));
const OnlineOrdersReport = lazy(() => import('./pages/reports/OnlineOrdersReport'));
const LaborCostReport = lazy(() => import('./pages/reports/LaborCostReport'));
const TaxCollectedReport = lazy(() => import('./pages/reports/TaxCollectedReport'));
const ItemSalesReport = lazy(() => import('./pages/reports/ItemSalesReport'));
const InvoiceAgingReport = lazy(() => import('./pages/reports/InvoiceAgingReport'));
const EmployeeTipEarnings = lazy(() => import('./pages/reports/EmployeeTipEarnings'));
const TipPoolAudit = lazy(() => import('./pages/reports/TipPoolAudit'));
const TipPoolPolicySettings = lazy(() => import('./pages/settings/TipPoolPolicySettings'));
// Phase 45.4d — franchisor reporting surface (groups the caller's org
// OWNS + monthly rollups + royalty statements). No client-side gate:
// the server scopes the group list to owner orgs (empty for everyone
// else) and the page renders friendly empty/403 notices.
const FranchisePage = lazy(() => import('./pages/franchise/FranchisePage'));

// Gate a route behind a permission; redirects Home if the user lacks it.
// The four gates below are exported (components only, so fast refresh is
// unaffected) for `navigation/routeGates.spec.ts`, which walks the real
// route tree and compares each gate against the SuiteNav tab config.
export function PermissionGate({ permission, children }: { permission: string; children: ReactNode }) {
  const allowed = usePermission(permission);
  if (!allowed) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function AnyPermissionGate({ permissions, children }: { permissions: string[]; children: ReactNode }) {
  const allowed = useAnyPermission(permissions);
  return allowed ? <>{children}</> : <Navigate to="/" replace />;
}

// Phase 41.1d — system-health is owner / platform_admin only, matching
// the API's RolesGuard on the outbox operator endpoints.
export function OwnerRoleGate({ children }: { children: ReactNode }) {
  const isOwner = useHasRole('owner');
  const isPlatformAdmin = useHasRole('platform_admin');
  if (!isOwner && !isPlatformAdmin) return <Navigate to="/" replace />;
  return <>{children}</>;
}

// Phase 38a — `/floor-plan` is manager-only. Users without
// `pos.manage_floor_plan` (host-tier roles) get redirected to `/host-stand`
// instead of the home page so they land on the surface they ARE entitled to.
// Inline rather than extending the shared `PermissionGate` because the
// host-stand redirect target is specific to this route.
export function FloorPlanRouteGate({ children }: { children: ReactNode }) {
  const allowed = usePermission('pos.manage_floor_plan');
  // Phase 64 — the host stand is the Scale `reservations` suite; below
  // Scale the floor role's own surface is the table-sessions list.
  const resolved = useFeatureFlagStore((s) => s.resolved);
  const hostStand = isFeatureEnabled('pos.reservations', resolved);
  if (!allowed) {
    return <Navigate to={hostStand ? '/host-stand' : '/floor-plan/sessions'} replace />;
  }
  return <>{children}</>;
}

/**
 * Phase 64 — the reservation book is the Scale `reservations` suite AND a
 * service provider's appointment book on every plan
 * (`pos.service_appointment`), exactly as the API's @OrFeatureEnabled on
 * ReservationController. The plan notice shows only when neither is on;
 * before the first flag sync the page renders optimistically, like
 * FeatureGate.
 */
export function ReservationBookGate({ children }: { children: ReactNode }) {
  const resolved = useFeatureFlagStore((s) => s.resolved);
  const hydrated = useFeatureFlagStore((s) => s.lastSyncedAtMs !== null);
  if (
    hydrated &&
    !isFeatureEnabled('pos.reservations', resolved) &&
    !isFeatureEnabled('pos.service_appointment', resolved)
  ) {
    return <FeatureUnavailableNotice featureId="pos.reservations" />;
  }
  return <>{children}</>;
}

/**
 * Phase 21 — gate a route behind a feature flag. Before the feature map
 * hydrates (first-ever render / fresh login), we render the children
 * optimistically — otherwise users entitled to a feature whose registry
 * default is `false` would get kicked off their own route during the
 * hydration window. Once the map lands and the feature is truly off,
 * we render an inline "feature not enabled" notice with a link to
 * `/settings/features` so the merchant understands why the page is empty
 * and how to turn it on. Previous behavior silently navigated to `/`
 * which made a refresh feel like the page mysteriously disappeared.
 */
export function FeatureGate({ featureId, anyFeatures, children }: { featureId: string; anyFeatures?: string[]; children: ReactNode }) {
  const resolved = useFeatureFlagStore((s) => s.resolved);
  const lastSyncedAtMs = useFeatureFlagStore((s) => s.lastSyncedAtMs);
  const hydrated = lastSyncedAtMs !== null;
  const enabled = anyFeatures?.length
    ? anyFeatures.some((id) => isFeatureEnabled(id, resolved))
    : isFeatureEnabled(featureId, resolved);
  if (hydrated && !enabled) {
    // Phase 64 — a plan lock gets an upgrade path, an owner's switch the
    // features page (FeatureUnavailableNotice tells them apart).
    return <FeatureUnavailableNotice featureId={featureId} />;
  }
  return <>{children}</>;
}

// Catch-all for unknown URLs. Deliberately NOT "Coming soon": a mistyped
// URL or a dead link is not a roadmap item, and telling merchants it is
// sends them looking for a feature that will never appear. (This replaced
// the single-use `Placeholder` component, whose body was hard-coded to
// that copy.)
function NotFoundPage() {
  return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center space-y-2">
        <h2 className="text-xl font-semibold">Page not found</h2>
        <p className="text-default-400 text-sm">
          We couldn't find that page. It may have moved, or the link may be
          out of date.
        </p>
        <Link to="/" className="text-primary text-sm font-medium hover:underline">
          Go to the Command Center
        </Link>
      </div>
    </div>
  );
}

// Suspense fallback shown while a lazily-loaded route chunk downloads.
// Intentionally dependency-free (no component imports) so it can't bloat
// the initial login entry chunk that this whole refactor exists to shrink.
function RouteFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div
        className="h-6 w-6 animate-spin rounded-full border-2 border-default-300 border-t-transparent"
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

export default function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterRouteGate />} />
      <Route path="/mfa" element={<MfaPage />} />
      {/* Phase 27a — public forgot/reset password flow. */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Phase 45.1b — SSO exchange-code landing page. */}
      <Route path="/sso/callback" element={<SsoCallbackPage />} />

      {/* Protected routes — wrapped in AppShell layout */}
      <Route element={<ProtectedRoute />}>
        {/*
          Phase 49 — hosted OAuth authorization endpoint. Deliberately
          OUTSIDE AppShell: granting a third-party app access to live
          business data shouldn't render inside the normal navigation
          chrome, where it reads as one more settings page. This is the
          URL published as `authorization_endpoint` in the OAuth
          discovery metadata.
        */}
        <Route
          path="developer/oauth/authorize"
          element={<PermissionGate permission="org.manage_developer"><OAuthConsentPage /></PermissionGate>}
        />
        <Route element={<AppShell />}>
          <Route path="commerce/catalog" element={<WorkspaceHub hub="catalog" title="Catalog & menus" description="Build your catalog, organize menus, import products, and manage pricing." />} />
          <Route path="commerce/orders" element={<WorkspaceHub hub="orders" title="Orders" description="Review sales, fulfillment, register sessions, and payment exceptions." />} />
          <Route path="commerce/online-store" element={<WorkspaceHub hub="onlineStore" title="Online store" description="Design your storefront, manage the online menu, and configure fulfillment." />} />
          <Route path="floor-plan/settings" element={<WorkspaceHub hub="service" title="Service settings" description="Configure reservations, table turn times, sections, and server assignments." />} />
          <Route path="roster/requests" element={<WorkspaceHub hub="requests" title="Requests" description="Employee requests and the approval queues your role can manage." />} />
          <Route path="roster/tips" element={<WorkspaceHub hub="tips" title="Tips" description="Tip pools, distribution rules, and reconciliation policy." />} />
          <Route path="marketing/engagement" element={<WorkspaceHub hub="engagement" title="Loyalty & messages" description="Customer rewards and the messages sent throughout the customer journey." />} />
          <Route path="settings/finance" element={<WorkspaceHub hub="finance" title="Finance" description="Payment processing, pricing policy, and accounting records." />} />

          {/* Home */}
          <Route index element={<CommandCenter />} />

          {/*
            Phase 35 Session 35.3a — help center route. Gated on
            `help.read` (backfilled onto every active role in 35.1a's
            migration so every authenticated user with the permission
            sees the surface). Three forms:
              - /help                        → default (first group, first article)
              - /help/category/:categorySlug → pin a category
              - /help/:articleId             → pin an article (parent resolves group)
            More-specific routes registered first so React Router's
            literal-segment match doesn't get caught by the :articleId
            param.
          */}
          <Route
            path="help/category/:categorySlug"
            element={
              <PermissionGate permission="help.read">
                <HelpCenter />
              </PermissionGate>
            }
          />
          {/*
            Phase 35 Session 35.5c-2 — merchant Support Inbox routes.
            Registered BEFORE `help/:articleId` so the literal
            `tickets` segment wins the matcher; otherwise React Router
            would resolve `/help/tickets` against the article-id
            catch-all + the help reader would render with `tickets` as
            an article id.
          */}
          <Route
            path="help/tickets"
            element={
              <PermissionGate permission="help.read">
                <TicketsListPage />
              </PermissionGate>
            }
          />
          <Route
            path="help/tickets/:ticketId"
            element={
              <PermissionGate permission="help.read">
                <TicketDetailPage />
              </PermissionGate>
            }
          />

          <Route
            path="help/:articleId"
            element={
              <PermissionGate permission="help.read">
                <HelpCenter />
              </PermissionGate>
            }
          />
          <Route
            path="help"
            element={
              <PermissionGate permission="help.read">
                <HelpCenter />
              </PermissionGate>
            }
          />

          {/*
            Phase 35 Session 35.5c-3 — platform-admin Support Inbox.
            Mounted under /admin/help/support-inbox so the URL prefix
            mirrors the email deep-link the 35.5c-1 SLA-breach template
            emits to ops teams. Per-id route registered AFTER the
            list route since both segments after support-inbox are
            literal vs. param — order matters when one is a prefix.
          */}
          <Route
            path="admin/help/support-inbox"
            element={
              <PermissionGate permission="help.manage_inbox">
                <SupportInboxPage />
              </PermissionGate>
            }
          />
          <Route
            path="admin/help/support-inbox/:ticketId"
            element={
              <PermissionGate permission="help.manage_inbox">
                <SupportTicketDetailPage />
              </PermissionGate>
            }
          />

          {/*
            Phase 35 Session 35.7-6 — platform-admin help analytics.
            Cross-tenant rollup + engagement-over-time + ticket-
            deflection metric. Gated on `help.manage_inbox` (the same
            authority surface that gates the support-admin inbox above
            — both span every merchant on the platform). Mounted under
            /admin/help-analytics rather than /admin/help/analytics so
            it's a sibling to /admin/help/support-inbox + the URL stays
            short for an oft-bookmarked surface.
          */}
          <Route
            path="admin/help-analytics"
            element={
              <PermissionGate permission="help.manage_inbox">
                <PlatformHelpAnalyticsPage />
              </PermissionGate>
            }
          />

          {/* Commerce suite */}
          <Route path="products" element={<PermissionGate permission="pos.manage_products"><ProductList /></PermissionGate>} />
          <Route path="products/new" element={<PermissionGate permission="pos.manage_products"><ProductForm /></PermissionGate>} />
          {/* Phase 45.3e — menu versions + scheduled publish. */}
          <Route
            path="products/menu-versions"
            element={
              <PermissionGate permission="pos.manage_products">
                <MenuVersionsPage />
              </PermissionGate>
            }
          />
          <Route path="products/:id/edit" element={<PermissionGate permission="pos.manage_products"><ProductForm /></PermissionGate>} />
          <Route path="categories" element={<PermissionGate permission="pos.manage_products"><CategoryManager /></PermissionGate>} />
          <Route path="orders" element={<PermissionGate permission="pos.view_all_orders"><OrderList /></PermissionGate>} />
          {/* Phase 18.4.7.4 — offline card payments reconciliation. Must
              come BEFORE `orders/:id` or the :id matcher catches it. */}
          <Route
            path="orders/offline-queue"
            element={
              <PermissionGate permission="auth.manage_users">
                <OfflineQueueList />
              </PermissionGate>
            }
          />
          {/* Phase 51.4 — dispute (chargeback) list over pos.payment_disputes. */}
          <Route
            path="payments/disputes"
            element={
              <PermissionGate permission="payments.view_disputes">
                <DisputesPage />
              </PermissionGate>
            }
          />
          {/* Phase 26.6b — merchant-facing live delivery-tracking board.
              Must come BEFORE orders/:id or the :id matcher catches it. */}
          <Route
            path="orders/active-deliveries"
            element={
              <PermissionGate permission="pos.view_all_orders">
                <FeatureGate featureId="pos.online_ordering"><ActiveDeliveriesPage /></FeatureGate>
              </PermissionGate>
            }
          />
          <Route path="orders/:id" element={<PermissionGate permission="pos.view_all_orders"><OrderDetail /></PermissionGate>} />

          {/* Gift Cards — reconciliation is now part of Offline Queue */}
          <Route path="gift-cards/reconciliation" element={<Navigate to="/orders/offline-queue?tab=gift-cards" replace />} />
          <Route
            path="gift-cards"
            element={
              <FeatureGate featureId="pos.gift_cards">
                <PermissionGate permission="pos.manage_gift_cards"><GiftCardList /></PermissionGate>
              </FeatureGate>
            }
          />
          <Route
            path="gift-cards/:id"
            element={
              <FeatureGate featureId="pos.gift_cards">
                <GiftCardDetail />
              </FeatureGate>
            }
          />

          {/* Discounts */}
          <Route path="discounts" element={<PermissionGate permission="pos.manage_discounts"><DiscountPresets /></PermissionGate>} />

          {/* Customers — Phase 24 user feedback (2026-04-18): gated on
              pos.customers so the routes 404 back to the dashboard home
              when the feature is off in the current org's preset /
              overrides. */}
          <Route
            path="customers"
            element={
              <AnyPermissionGate permissions={["customers.manage","pos.checkout","marketing.view_campaigns","loyalty.view_accounts"]}><FeatureGate featureId="pos.customers">
                <CustomerList />
              </FeatureGate></AnyPermissionGate>
            }
          />
          <Route
            path="customers/:id"
            element={
              <AnyPermissionGate permissions={["customers.manage","pos.checkout","marketing.view_campaigns","loyalty.view_accounts"]}><FeatureGate featureId="pos.customers">
                <CustomerDetail />
              </FeatureGate></AnyPermissionGate>
            }
          />

          {/* Floor & Table */}
          <Route
            path="floor-plan"
            element={
              <FeatureGate featureId="pos.floor_plan">
                <FloorPlanRouteGate>
                  <PermissionGate permission="pos.manage_floor_plan"><FloorPlanEditorPage /></PermissionGate>
                </FloorPlanRouteGate>
              </FeatureGate>
            }
          />
          <Route
            path="floor-plan/sessions"
            element={
              <FeatureGate featureId="pos.floor_plan">
                <PermissionGate permission="pos.checkout"><TableSessions /></PermissionGate>
              </FeatureGate>
            }
          />

          {/* KDS Monitor (read-only) */}
          <Route
            path="kds-monitor"
            element={
              <FeatureGate featureId="kds.stations">
                <PermissionGate permission="kds.view">
                  <KdsMonitor />
                </PermissionGate>
              </FeatureGate>
            }
          />

          {/* Registers */}
          <Route path="registers" element={<PermissionGate permission="pos.manage_register"><RegisterSessionList /></PermissionGate>} />
          <Route path="registers/:id" element={<PermissionGate permission="pos.manage_register"><RegisterDetail /></PermissionGate>} />

          {/* Bar tabs — Phase 18.4.6 read-only admin view. Closing,
              walkout, and open flows live on the POS terminal. */}
          <Route
            path="bar-tabs"
            element={
              <FeatureGate featureId="pos.bar_tabs">
                <PermissionGate permission="pos.close_tab">
                  <BarTabAdminList />
                </PermissionGate>
              </FeatureGate>
            }
          />

          {/* Tax */}
          <Route path="tax" element={<PermissionGate permission="tax.configure"><TaxDashboard /></PermissionGate>} />

          {/* Invoicing suite */}
          <Route path="invoices" element={<PermissionGate permission="invoice.view_all"><InvoiceList /></PermissionGate>} />
          {/* Phase 44.4c — house accounts (charge-to-account AR). */}
          <Route path="invoices/house-accounts" element={<PermissionGate permission="invoice.view_all"><HouseAccountsPage /></PermissionGate>} />
          <Route path="invoices/new" element={<PermissionGate permission="invoice.create_invoice"><InvoiceForm /></PermissionGate>} />
          <Route path="invoices/:id/edit" element={<PermissionGate permission="invoice.create_invoice"><InvoiceForm /></PermissionGate>} />
          <Route path="quotes" element={<FeatureGate featureId="invoice.quotes"><PermissionGate permission="invoice.view_all"><QuoteList /></PermissionGate></FeatureGate>} />
          <Route path="quotes/new" element={<FeatureGate featureId="invoice.quotes"><PermissionGate permission="invoice.create_quote"><QuoteForm /></PermissionGate></FeatureGate>} />
          <Route path="quotes/:id/edit" element={<FeatureGate featureId="invoice.quotes"><PermissionGate permission="invoice.create_quote"><QuoteForm /></PermissionGate></FeatureGate>} />

          {/* ── Field Service suite (Phase 50) ─────────────────────
               50.1 ships Field Settings; 50.2 adds Jobs; 50.3 adds the
               Visits calendar + Dispatch board; Agreements routes land
               with their session (50.6). Every route carries the SAME
               permission its SuiteNav tab hides on — until 2026-09-15
               they were feature-gated only, so a field_tech could reach
               the CRM board or Costing by URL. `navigation/routeGates.spec.ts`
               keeps the two in step. */}
          <Route
            path="field/jobs"
            element={
              <FeatureGate featureId="field_service.core">
                <PermissionGate permission="field_service.view_jobs">
                  <JobsList />
                </PermissionGate>
              </FeatureGate>
            }
          />
          <Route
            path="field/jobs/:id"
            element={
              <FeatureGate featureId="field_service.core">
                <PermissionGate permission="field_service.view_jobs">
                  <JobDetail />
                </PermissionGate>
              </FeatureGate>
            }
          />
          <Route
            path="field/visits"
            element={
              <FeatureGate featureId="field_service.visits">
                <PermissionGate permission="field_service.view_jobs">
                  <VisitsCalendar />
                </PermissionGate>
              </FeatureGate>
            }
          />
          <Route
            path="field/dispatch"
            element={
              <FeatureGate featureId="field_service.dispatch">
                <PermissionGate permission="field_service.dispatch">
                  <DispatchBoard />
                </PermissionGate>
              </FeatureGate>
            }
          />
          {/* Phase 50.4 — job media library. */}
          <Route
            path="field/media"
            element={
              <FeatureGate featureId="field_service.media">
                <PermissionGate permission="field_service.view_jobs">
                  <MediaLibraryPage />
                </PermissionGate>
              </FeatureGate>
            }
          />
          {/* Phase 50.8 — CRM kanban (deals pipeline). */}
          <Route
            path="field/crm"
            element={
              <FeatureGate featureId="field_service.crm">
                <PermissionGate permission="field_service.manage_crm">
                  <CrmPage />
                </PermissionGate>
              </FeatureGate>
            }
          />
          {/* Phase 50.8 — internal alerts inbox (claim / escalate). */}
          <Route
            path="field/alerts"
            element={
              <FeatureGate featureId="field_service.alerts">
                <PermissionGate permission="field_service.manage_alerts">
                  <AlertsPage />
                </PermissionGate>
              </FeatureGate>
            }
          />
          {/* Phase 50.6 — recurring service agreements. */}
          <Route
            path="field/agreements"
            element={
              <FeatureGate featureId="field_service.agreements">
                <PermissionGate permission="field_service.view_jobs">
                  <AgreementsPage />
                </PermissionGate>
              </FeatureGate>
            }
          />
          {/* Phase 50.7 — costing + owner-tier P&L/performance reports. */}
          <Route path="field/my-labor" element={<FeatureGate featureId="field_service.costing"><PermissionGate permission="field_service.record_own_labor"><MyLaborPage /></PermissionGate></FeatureGate>} />
          <Route
            path="field/costing"
            element={
              <FeatureGate featureId="field_service.costing">
                <PermissionGate permission="field_service.manage_labor">
                  <CostingPage />
                </PermissionGate>
              </FeatureGate>
            }
          />
          <Route
            path="field/reports"
            element={
              <FeatureGate featureId="field_service.costing">
                <PermissionGate permission="field_service.view_job_costs">
                  <FsReportsPage />
                </PermissionGate>
              </FeatureGate>
            }
          />
          <Route
            path="field/settings"
            element={
              <FeatureGate featureId="field_service.core">
                <PermissionGate permission="field_service.manage_settings">
                  <FieldSettingsPage />
                </PermissionGate>
              </FeatureGate>
            }
          />

          {/* ── Roster Management suite ─────────────────────────────
               Renamed from "Payroll". UserManager + RoleManager MOVED
               here from Settings. Timesheets + Schedules remain
               placeholders until the external-integration pipeline
               ships; Integrations is a static provider catalog today.
               "Run Payroll" is explicitly dropped — platform role is
               to surface time-management data, not own pay
               calculation. */}
          <Route
            path="roster/employees"
            element={
              <PermissionGate permission="auth.manage_users">
                <UserManager />
              </PermissionGate>
            }
          />
          <Route
            path="roster/roles"
            element={
              <AnyPermissionGate permissions={["auth.manage_users","auth.manage_roles"]}><RoleManager /></AnyPermissionGate>
            }
          />
          <Route path="roster/timesheets" element={<PermissionGate permission="payroll.view_all_entries"><TimesheetsPage /></PermissionGate>} />
          {/* Phase 48.1 — the real schedules surface (week-grid builder).
               Replaces the placeholder redirect removed 2026-07-08. */}
          <Route
            path="roster/schedules"
            element={
              <PermissionGate permission="payroll.manage_schedules">
                <SchedulesPage />
              </PermissionGate>
            }
          />
          {/* Phase 33.5b-2 — Employee self-service portal: the
               manager-facing time-off approval queue and the
               employee-facing /roster/my-requests page (time-off +
               availability; shift-swap UI removed 2026-07-08). */}
          <Route
            path="roster/time-off"
            element={
              <PermissionGate permission="payroll.approve_employee_requests">
                <TimeOffApprovalQueue />
              </PermissionGate>
            }
          />
          {/* Phase 47.0 — shift-swap approval queue restored (was removed
               2026-07-08 while no employee surface could create swaps).
               The ESS Requests screen in the POS apps revives swap
               creation, so managers need the queue back. Old
               /roster/swap-requests links land here. */}
          <Route
            path="roster/swap-approvals"
            element={
              <PermissionGate permission="payroll.approve_employee_requests">
                <ShiftSwapApprovalQueue />
              </PermissionGate>
            }
          />
          <Route path="roster/swap-requests" element={<Navigate to="/roster/swap-approvals" replace />} />
          {/* Phase 48.2 — open-shift claim approvals. */}
          <Route
            path="roster/claim-approvals"
            element={
              <PermissionGate permission="payroll.approve_employee_requests">
                <OpenShiftClaimsQueue />
              </PermissionGate>
            }
          />
          <Route path="roster/my-requests" element={<PermissionGate permission="payroll.request_time_off"><MyRequests /></PermissionGate>} />

          {/* Legacy redirects so existing bookmarks / external links
               don't 404. Three payroll → roster redirects plus two
               Settings → Roster moves. Phase 28.5d-1 also redirects
               the retired /roster/integrations route so pilot merchants
               on pre-launch builds land cleanly on the timesheets tab. */}
          <Route path="roster/integrations" element={<Navigate to="/roster/timesheets" replace />} />
          <Route path="payroll" element={<Navigate to="/roster/timesheets" replace />} />
          <Route path="payroll/schedules" element={<Navigate to="/roster/schedules" replace />} />
          <Route path="payroll/run" element={<Navigate to="/roster/timesheets" replace />} />

          {/* Phase 27.5 — Inventory suite */}
          <Route
            path="inventory"
            element={
              <PermissionGate permission="inventory.view_stock">
                <InventoryOverview />
              </PermissionGate>
            }
          />
          {/* Inventory reorg 2026-05-08 — Procurement hub (Vendors + POs).
              Lives at /inventory/procurement; SuiteNav lights up via
              pathAliases match across vendors + purchase-orders. */}
          <Route
            path="inventory/procurement"
            element={
              <ProcurementHub />
            }
          />
          <Route
            path="inventory/stock"
            element={
              <PermissionGate permission="inventory.view_stock">
                <StockLevelList />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/stock/:id"
            element={
              <PermissionGate permission="inventory.view_stock">
                <StockItemDetail />
              </PermissionGate>
            }
          />
          {/* 2026-09-09 — Items + Levels are tabs on one page; the old
              /inventory/levels URL lands on that tab so bookmarks survive. */}
          <Route
            path="inventory/levels"
            element={<Navigate to="/inventory/stock?tab=levels" replace />}
          />
          <Route
            path="inventory/vendors"
            element={
              <PermissionGate permission="inventory.view_vendors">
                <VendorList />
              </PermissionGate>
            }
          />
          {/* Phase 27.5b — Purchase Orders + Receiving. Detail page
              hosts the receive modal; no separate receiving route. */}
          <Route
            path="inventory/purchase-orders/new"
            element={
              <PermissionGate permission="inventory.manage_purchase_orders">
                <PurchaseOrderForm />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/purchase-orders/:id/edit"
            element={
              <PermissionGate permission="inventory.manage_purchase_orders">
                <PurchaseOrderForm />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/purchase-orders/:id"
            element={
              <PermissionGate permission="inventory.view_purchase_orders">
                <PurchaseOrderDetail />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/purchase-orders"
            element={
              <PermissionGate permission="inventory.view_purchase_orders">
                <PurchaseOrderList />
              </PermissionGate>
            }
          />
          {/* Phase 56.2 — cycle-count sessions. */}
          <Route
            path="inventory/counts"
            element={
              <PermissionGate permission="inventory.view_stock">
                <CountSessionList />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/counts/:id"
            element={
              <PermissionGate permission="inventory.view_stock">
                <CountSheetPage />
              </PermissionGate>
            }
          />
          {/* Phase 56.6 — invoice scan-to-receive. */}
          <Route
            path="inventory/invoice-scans"
            element={
              <PermissionGate permission="inventory.receive_stock">
                <InvoiceScanList />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/invoice-scans/:id"
            element={
              <PermissionGate permission="inventory.receive_stock">
                <InvoiceScanReview />
              </PermissionGate>
            }
          />
          {/* Phase 56.3 — transfer lifecycle. */}
          <Route
            path="inventory/transfers"
            element={
              <PermissionGate permission="inventory.view_stock">
                <TransferList />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/transfers/:id"
            element={
              <PermissionGate permission="inventory.view_stock">
                <TransferDetail />
              </PermissionGate>
            }
          />
          {/* Phase 27.5c — Adjustments + transfers + waste + valuation. */}
          <Route
            path="inventory/adjustments"
            element={
              <PermissionGate permission="inventory.adjust_stock">
                <AdjustmentPage />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/waste"
            element={
              <PermissionGate permission="inventory.view_stock">
                <WasteReport />
              </PermissionGate>
            }
          />
          <Route
            path="inventory/valuation"
            element={
              <PermissionGate permission="inventory.view_stock">
                <InventoryValuation />
              </PermissionGate>
            }
          />
          {/* Phase 31 Session 31.2c — ingredient demand prediction.
              Lives under /inventory because that's where merchants
              think about reordering. Gated on analytics.view (the
              same permission that gates /analytics/forecast — same
              math infrastructure, separate consumer surface). */}
          <Route
            path="inventory/ingredient-forecast"
            element={
              <PermissionGate permission="analytics.view">
                <IngredientForecast />
              </PermissionGate>
            }
          />
          {/* Phase 29.1f — Recipes. Phase 29.2b — Menu Engineering.
              Literal segments (/new, /:id/edit, /menu-engineering)
              registered BEFORE the list route to win the matcher. */}
          <Route
            path="inventory/recipes/new"
            element={
              <PermissionGate permission="inventory.manage_recipes">
                <FeatureGate featureId="inventory.recipe_costing">
                  <RecipeForm />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route
            path="inventory/recipes/:id/edit"
            element={
              <PermissionGate permission="inventory.manage_recipes">
                <FeatureGate featureId="inventory.recipe_costing">
                  <RecipeForm />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route
            path="inventory/recipes/menu-engineering"
            element={
              <PermissionGate permission="inventory.view_recipes">
                <FeatureGate featureId="inventory.recipe_costing">
                  <MenuEngineering />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route
            path="inventory/recipes/food-cost"
            element={
              <PermissionGate permission="inventory.view_recipes">
                <FeatureGate featureId="inventory.recipe_costing">
                  <FoodCostDashboard />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route
            path="inventory/recipes"
            element={
              <PermissionGate permission="inventory.view_recipes">
                {/* Phase 64 — recipes, menu engineering and food cost are the Scale `recipe_costing` suite. */}
                <FeatureGate featureId="inventory.recipe_costing">
                  <RecipeList />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Reports suite */}
          <Route path="reports" element={<AnyPermissionGate permissions={['reporting.view_sales', 'reporting.view_labor', 'reporting.view_tax', 'reporting.view_financial', 'pos.view_tip_reports']}><ReportSelector /></AnyPermissionGate>} />
          <Route path="reports/sales" element={<PermissionGate permission="reporting.view_sales"><DailySalesReport /></PermissionGate>} />
          <Route path="reports/eod" element={<PermissionGate permission="reporting.view_sales"><EODReconciliationReport /></PermissionGate>} />
          <Route path="reports/register-closes" element={<PermissionGate permission="reporting.view_sales"><RegisterClosesReport /></PermissionGate>} />
          <Route path="reports/online-orders" element={<PermissionGate permission="reporting.view_sales"><OnlineOrdersReport /></PermissionGate>} />
          <Route path="reports/labor" element={<PermissionGate permission="reporting.view_labor"><LaborCostReport /></PermissionGate>} />
          <Route path="reports/tax" element={<PermissionGate permission="reporting.view_tax"><TaxCollectedReport /></PermissionGate>} />
          <Route path="reports/items" element={<PermissionGate permission="reporting.view_sales"><ItemSalesReport /></PermissionGate>} />
          <Route path="reports/invoices" element={<PermissionGate permission="reporting.view_financial"><InvoiceAgingReport /></PermissionGate>} />

          {/* Phase 36.5.6 — tip reconciliation reports. Both gated on
              `pos.view_tip_reports` (every role with tip visibility);
              the audit page's `:poolId?` is optional so a generic
              "Tip pool audit" SuiteNav entry resolves to the first
              active pool via auto-pick + replace-navigate. */}
          <Route
            path="reports/tip-earnings"
            element={
              <PermissionGate permission="pos.view_tip_reports">
                <EmployeeTipEarnings />
              </PermissionGate>
            }
          />
          <Route
            path="reports/tip-audit/:poolId?"
            element={
              <PermissionGate permission="pos.view_tip_reports">
                <TipPoolAudit />
              </PermissionGate>
            }
          />

          {/* Phase 45.4d — franchise reporting (rollups + royalties for
              groups the caller's org owns). Lives in the reports suite's
              tab strip. Deliberately ungated client-side: no existing
              permission code fits the owner/admin role tier the service
              enforces, and the page itself renders the no-groups empty
              state / 403 notice. */}
          <Route path="franchise" element={<OwnerRoleGate><FranchisePage /></OwnerRoleGate>} />

          {/* Phase 31 Session 31.2b — Analytics suite. Forecast page gated
              on `analytics.view` (seeded by migration 0055; backfilled
              onto every role with `reporting.view_sales`). 31.3 adds
              labor forecasting; 31.4 adds the benchmarking page (gated
              on `analytics.manage_benchmarking`). */}
          <Route
            path="analytics/forecast"
            element={
              <PermissionGate permission="analytics.view">
                <ForecastPage />
              </PermissionGate>
            }
          />
          {/* Phase C — conversational AI assistant (read tools + flag-gated,
              human-confirmed write actions). Same analytics.view gate as the
              forecast page; write actions gate server-side on the
              reporting.ai_write_actions flag. Phase 46.2 — also gated on the
              `analytics.assistant` per-org kill-switch (default ON for
              Growth+); the server 403s both chat endpoints when it's off. */}
          <Route
            path="analytics/assistant"
            element={
              <PermissionGate permission="analytics.view">
                <FeatureGate featureId="analytics.assistant">
                  <AssistantPage />
                </FeatureGate>
              </PermissionGate>
            }
          />
          {/* Legacy onboarding setup-assistant URL. The setup concierge is now
              the "Setup" inner tab of the unified Assistant page (merchant
              feedback 2026-06-29), so this redirects there for bookmark
              continuity. */}
          <Route
            path="setup/assistant"
            element={<Navigate to="/analytics/assistant" replace />}
          />
          {/* Phase 31 Session 31.3 — labor demand forecast. Composes
              31.2a traffic forecast × merchant payroll config into
              recommended hours + cost + overtime risk. Same
              analytics.view permission gate as the demand forecast. */}
          <Route
            path="analytics/labor"
            element={
              <PermissionGate permission="analytics.view">
                <LaborForecastPage />
              </PermissionGate>
            }
          />
          {/* Phase 31 Session 31.4 — multi-vertical benchmarking.
              Caller's percentile rank against opted-in peers in their
              vertical. Read gated on analytics.view; opt-in mutation
              gated server-side on analytics.manage_benchmarking. */}
          <Route
            path="analytics/benchmarks"
            element={
              <PermissionGate permission="analytics.view">
                <BenchmarksPage />
              </PermissionGate>
            }
          />

          {/* Automation */}
          <Route path="automation" element={<PermissionGate permission="automation.view_logs"><AutomationList /></PermissionGate>} />
          <Route path="automation/:id" element={<PermissionGate permission="automation.view_logs"><AutomationDetail /></PermissionGate>} />

          {/* Phase 30 Session 30.1b — Marketing campaigns. Reads gated on
              marketing.view_campaigns; the form's create + update + schedule
              actions are still gated server-side via marketing.manage_campaigns
              + marketing.send_campaigns (the form surfaces server 403s
              inline so non-managers see "Cannot edit" rather than a route
              redirect mid-edit). */}
          <Route
            path="marketing/campaigns/new"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.manage_campaigns">
                <CampaignForm />
              </PermissionGate></FeatureGate>
            }
          />
          <Route
            path="marketing/campaigns/:id/edit"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.view_campaigns">
                <CampaignForm />
              </PermissionGate></FeatureGate>
            }
          />
          {/* Phase 30.3b — recipients drill-down. */}
          <Route
            path="marketing/campaigns/:id/recipients"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.view_campaigns">
                <CampaignRecipients />
              </PermissionGate></FeatureGate>
            }
          />
          <Route
            path="marketing/campaigns"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.view_campaigns">
                <CampaignList />
              </PermissionGate></FeatureGate>
            }
          />

          {/* Phase 30 Session 30.2c — Marketing segments. Same gating
              pattern as campaigns; create gates on manage_campaigns
              (segments are part of the campaigns authority surface). */}
          <Route
            path="marketing/segments/new"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.manage_campaigns">
                <SegmentForm />
              </PermissionGate></FeatureGate>
            }
          />
          <Route
            path="marketing/segments/:id/edit"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.view_campaigns">
                <SegmentForm />
              </PermissionGate></FeatureGate>
            }
          />
          <Route
            path="marketing/segments"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.view_campaigns">
                <SegmentList />
              </PermissionGate></FeatureGate>
            }
          />

          {/* Phase 30.4 — analytics overview. */}
          <Route
            path="marketing/analytics"
            element={
              <FeatureGate featureId="marketing.campaigns"><PermissionGate permission="marketing.view_campaigns">
                <MarketingAnalytics />
              </PermissionGate></FeatureGate>
            }
          />

          {/* Settings suite — reorganized 2026-05-08 from 21 flat tabs into
              5 hub tabs. Hub URLs (/settings, /settings/operations, etc.)
              render the consolidated landing page; every leaf URL stays
              registered below for bookmark continuity. SuiteNav's
              `pathAliases` config lights up the active hub tab when the
              user lands on any of its leaves. */}
          <Route path="settings" element={<OrganizationHub />} />
          <Route path="settings/organization" element={<PermissionGate permission="auth.manage_org"><OrgSettings /></PermissionGate>} />
          {/* Phase 45.3e — location groups. Gated on `auth.manage_org`
              (org-structure authority), matching the API controller. */}
          <Route
            path="settings/organization/location-groups"
            element={
              <PermissionGate permission="auth.manage_org">
                <LocationGroupsPage />
              </PermissionGate>
            }
          />
          {/* Phase 66.16 — the pilot record and readout, for whoever runs setup. */}
          <Route
            path="pilot"
            element={
              <PermissionGate permission="auth.manage_org">
                <PilotPage />
              </PermissionGate>
            }
          />
          <Route path="settings/operations" element={<OperationsHub />} />
          <Route path="settings/hardware" element={<HardwareHub />} />
          <Route path="settings/admin" element={<AdminHub />} />
          <Route path="settings/vertical" element={<PermissionGate permission="auth.manage_org"><VerticalPicker /></PermissionGate>} />
          <Route path="settings/branding" element={<PermissionGate permission="auth.manage_branding"><BrandingPage /></PermissionGate>} />
          {/* Phase 66.6 — back from Stripe's authorize page, with the code to finish the connection. */}
          <Route
            path="settings/payments/stripe-connect/callback"
            element={
              <PermissionGate permission="marketplace.manage_connected_accounts">
                <StripeConnectCallbackPage />
              </PermissionGate>
            }
          />
          {/* Phase 38.1 — Stripe Connect merchant self-onboarding. */}
          <Route
            path="settings/payments/stripe-connect"
            element={
              <PermissionGate permission="marketplace.manage_connected_accounts">
                <StripeConnectPage />
              </PermissionGate>
            }
          />
          {/* Phase 44.1 — monthly processing statements. */}
          <Route
            path="settings/payments/statement"
            element={
              <PermissionGate permission="payments.view_statements">
                <ProcessingStatementPage />
              </PermissionGate>
            }
          />
          {/* Phase 44.2c — dual pricing / cash discounting / surcharging. */}
          <Route
            path="settings/payments/dual-pricing"
            element={
              <PermissionGate permission="marketplace.manage_connected_accounts">
                <DualPricingPage />
              </PermissionGate>
            }
          />
          {/* Phase 30 Session 30.5 — storefront custom-domain admin. */}
          {/* Phase 64 — the storefront is the Growth `online_ordering` suite. */}
          <Route
            path="settings/storefront"
            element={
              <PermissionGate permission="auth.manage_branding"><FeatureGate featureId="pos.online_ordering">
                <StorefrontConfigPage />
              </FeatureGate></PermissionGate>
            }
          />
          {/* Phase 33 Session 33.2 sub-commit B — QR Code Manager. */}
          <Route
            path="settings/qr-codes"
            element={
              <PermissionGate permission="pos.manage_floor_plan">
                {/* Phase 64 — QR order-and-pay rides the storefront. */}
                <FeatureGate featureId="pos.online_ordering">
                  <QrCodesPage />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route path="settings/features" element={<PermissionGate permission="auth.manage_features"><FeatureFlagsPage /></PermissionGate>} />
          {/* Settings reorg 2026-05-08 — /settings/integrations now renders
              the hub landing page (not the accounting-specific page). The
              accounting QuickBooks/Xero connector list relocates to
              /settings/integrations/accounting, gated on the same
              permission as before. The OAuth callback path is unchanged
              (still /settings/integrations/:provider/callback) since the
              backend's redirect URL contract uses that shape. */}
          <Route path="settings/integrations" element={<IntegrationsHub />} />
          <Route
            path="settings/integrations/accounting"
            element={
              <PermissionGate permission="org.manage_integrations">
                <IntegrationsPage />
              </PermissionGate>
            }
          />
          {/* Phase 43.1c — competitor data import (Square/Clover/Toast). */}
          <Route
            path="settings/integrations/import"
            element={
              <PermissionGate permission="org.manage_integrations">
                <DataImportPage />
              </PermissionGate>
            }
          />
          {/* 2026-09-29 — courier accounts: online delivery orders dispatch
              and bill on the MERCHANT's own DoorDash Drive / Uber Direct
              account, per org or per location. Online-ordering suite. */}
          <Route
            path="settings/integrations/delivery"
            element={
              <PermissionGate permission="org.manage_integrations">
                <FeatureGate featureId="pos.online_ordering">
                  <DeliveryCouriersPage />
                </FeatureGate>
              </PermissionGate>
            }
          />
          {/* Phase 65.2 — shipping: rates and labels on the MERCHANT's own
              EasyPost account, the shipping rules, boxes and a rate check.
              Behind its own flag (`pos.shipping`, default off). */}
          <Route
            path="settings/integrations/shipping"
            element={
              <PermissionGate permission="org.manage_integrations">
                <FeatureGate featureId="pos.shipping">
                  <ShippingSettingsPage />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route
            path="settings/integrations/:provider/callback"
            element={
              <PermissionGate permission="org.manage_integrations">
                <IntegrationOAuthCallback />
              </PermissionGate>
            }
          />
          {/* Phase 28.11 — merchant-authored transactional message
              templates. Owner-tier permission; platform_admin gets
              it via the wildcard branch. */}
          <Route
            path="settings/messaging/templates"
            element={
              <PermissionGate permission="messaging.manage_templates">
                <MessagingTemplates />
              </PermissionGate>
            }
          />
          <Route path="settings/custom-fields" element={<PermissionGate permission="org.manage_custom_fields"><CustomFieldsPage /></PermissionGate>} />
          <Route path="settings/developer" element={<PermissionGate permission="org.manage_developer"><DeveloperAppsPage /></PermissionGate>} />
          {/* Item 42.4a — the merchant side of consent: apps installed
              INTO this org, and the ability to remove them. Distinct
              from settings/developer (apps this org BUILDS), which is
              also part of the public agent-discovery contract. */}
          <Route
            path="settings/installed-apps"
            element={<PermissionGate permission="org.manage_developer"><InstalledAppsPage /></PermissionGate>}
          />
          {/* Phase 57.1 — chart of accounts + the posting mappings the
              ledger resolves every account through. */}
          <Route path="settings/accounting" element={<PermissionGate permission="accounting.view_ledger"><AccountingPage /></PermissionGate>} />
          {/* Phase 57.2 — the ledger itself: journal + trial balance.
              Lives outside /settings because it's daily work, not
              configuration. */}
          <Route path="accounting/ledger" element={<PermissionGate permission="accounting.view_ledger"><LedgerPage /></PermissionGate>} />
          {/* Phase 34 Session 34.4b — audit log + usage analytics dashboard
              pages. Audit-log read gates on auth.view_audit_log; usage
              analytics gates on auth.view_usage_analytics (both seeded by
              34.4a migration 0060). Each page renders a friendly
              permission-denied banner internally too — defense in depth. */}
          <Route
            path="settings/audit-log"
            element={
              <PermissionGate permission="auth.view_audit_log">
                {/* Phase 64 — the Scale `audit_log` suite. */}
                <FeatureGate featureId="audit.log">
                  <AuditLogPage />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route
            path="settings/usage-analytics"
            element={
              <PermissionGate permission="auth.view_usage_analytics">
                <UsageAnalyticsPage />
              </PermissionGate>
            }
          />
          {/* Phase 35 Session 35.7-5 — merchant help-analytics surface.
              Reuses `auth.view_usage_analytics` as the read gate so the
              same back-office reporting tier (owner + shift_lead +
              hr_admin + marketing_mgr + it_admin per the 34.4a backfill)
              that already sees usage analytics also sees help analytics.
              GDPR wipe action inside the page additionally gates on
              `auth.manage_users` (owner-tier only). */}
          <Route
            path="settings/help-analytics"
            element={
              <PermissionGate permission="auth.view_usage_analytics">
                <HelpAnalyticsPage />
              </PermissionGate>
            }
          />
          <Route path="settings/locations" element={<PermissionGate permission="auth.manage_users"><LocationList /></PermissionGate>} />
          {/* Phase 28.9a companion — Settings > Users + Settings > Roles
              MOVED under the Roster Management suite. Legacy paths
              redirect so existing bookmarks keep working. */}
          <Route path="settings/users" element={<Navigate to="/roster/employees" replace />} />
          <Route path="settings/roles" element={<Navigate to="/roster/roles" replace />} />
          <Route path="settings/devices" element={<PermissionGate permission="hardware.manage_fleet"><DeviceList /></PermissionGate>} />
          <Route
            path="settings/notifications"
            element={
              <PermissionGate permission="auth.edit_own_profile">
                <NotificationSettings />
              </PermissionGate>
            }
          />
          {/* Phase 68.4 — Settings → Texting. Off for every business until
              68.8 releases it; agreeing to the fee and filing also need
              billing.manage, which the page checks. */}
          <Route
            path="settings/texting"
            element={
              <PermissionGate permission="messaging.manage_texting">
                <FeatureGate featureId="messaging.customer_texts">
                  <TextingSettingsPage />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route path="settings/devices/:id" element={<PermissionGate permission="hardware.manage_fleet"><DeviceDetail /></PermissionGate>} />
          {/* Phase 62 follow-up — KDS stations (create / pair / route /
              deactivate). Same gates as the /kds-monitor it feeds; edits
              additionally need kds.manage_stations inside the page. */}
          <Route
            path="settings/kds-stations"
            element={
              <FeatureGate featureId="kds.stations">
                <PermissionGate permission="kds.view">
                  <KdsStationsPage />
                </PermissionGate>
              </FeatureGate>
            }
          />
          {/* Phase 24.3c — forecourt pump management. Route stays
              registered regardless of the feature flag so flipping it
              on doesn't require a redeploy; PumpsPage renders an
              opt-in CTA when `pos.pump_integration` is off. */}
          <Route path="settings/pumps" element={<PermissionGate permission="pos.manage_pumps"><PumpsPage /></PermissionGate>} />

          {/* Phase 28 Session 28.5 — Turn-time ladder per location.
              Page handles the permission gate internally (read-only
              banner when the user lacks pos.manage_floor_plan). */}
          <Route
            path="settings/turn-times"
            element={
              <PermissionGate permission="pos.manage_floor_plan">
                {/* Phase 64 — floor-plan setup: Growth `table_service`. */}
                <FeatureGate featureId="pos.floor_plan">
                  <TurnTimesSettings />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Phase 28 Session 28.7b — reservation settings per location.
              Surfaces the booking-engine knobs + the new 28.7b reminder
              enable/cadence flags. */}
          <Route
            path="settings/reservations"
            element={
              <PermissionGate permission="pos.manage_floor_plan">
                {/* Phase 64 — the Scale `reservations` suite. */}
                <FeatureGate featureId="pos.reservations" anyFeatures={['pos.reservations', 'pos.online_booking']}>
                  <ReservationSettings />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Phase 28 Session 28.7c — reservation list + detail. Gated on
              pos.checkout so every front-of-house role can manage the
              surface; 28.9 host stand consumes the same endpoints. */}
          <Route
            path="reservations"
            element={
              <PermissionGate permission="pos.checkout">
                <ReservationBookGate>
                  <ReservationsPage />
                </ReservationBookGate>
              </PermissionGate>
            }
          />
          <Route
            path="reservations/:id"
            element={
              <PermissionGate permission="pos.checkout">
                <ReservationBookGate>
                  <ReservationDetail />
                </ReservationBookGate>
              </PermissionGate>
            }
          />

          {/* Phase 28.9b — Unified host stand at /host-stand. Gated on
              pos.host_stand, the narrower read permission 28.9a seeded
              + backfilled onto every pos.checkout holder. 28.9b-1 ships
              the list-view shell; 28.9b-2 adds timeline + drag-to-seat
              + real-time. */}
          <Route
            path="host-stand"
            element={
              <PermissionGate permission="pos.host_stand">
                {/* Phase 64 — the Scale `reservations` suite. */}
                <FeatureGate featureId="pos.reservations">
                  <HostStand />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Phase 28 Session 28.6a — reusable section templates.
              Gated on floor_plan.assign_sections (backfilled onto every
              role that has pos.manage_floor_plan + granted to owner +
              shift_lead in the default seed). */}
          <Route
            path="settings/section-templates"
            element={
              <PermissionGate permission="floor_plan.assign_sections">
                <FeatureGate featureId="pos.floor_plan">
                  <SectionTemplates />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Phase 28 Session 28.6b — server roster. Gated on
              floor_plan.assign_sections (same authority that manages
              sections). When 28.9 host-stand lands, `pos.host_stand` can
              relax the read gate for front-of-house staff. */}
          <Route
            path="settings/roster"
            element={
              <PermissionGate permission="floor_plan.assign_sections">
                <FeatureGate featureId="pos.floor_plan">
                  <RosterPage />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Phase 28 Session 28.2c — Loyalty programs admin. Gated on
              loyalty.manage_program so non-owner users don't reach the
              page via URL surgery. Phase 64 — and on `pos.loyalty`, the
              Growth `loyalty` suite. */}
          <Route
            path="settings/loyalty"
            element={
              <PermissionGate permission="loyalty.manage_program">
                <FeatureGate featureId="pos.loyalty">
                  <LoyaltyPrograms />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Phase 36.5.3b — tip pool admin under the Operations hub.
              Read access surfaces via `pos.view_tip_reports` (every role
              with tip visibility); write actions inside the page enforce
              `pos.configure_tip_pools`, and the manual Calculate-now
              button additionally checks `pos.manage_tips`. */}
          <Route
            path="settings/tip-pools"
            element={
              <PermissionGate permission="pos.view_tip_reports">
                <TipPools />
              </PermissionGate>
            }
          />

          {/* Phase 36.5.6 — Tip pool reconciliation policy. Read access
              wraps to the same `pos.view_tip_reports` so the form is
              visible; the save action gates on `auth.manage_org` per
              the controller (TipPoolPolicyService.update). Without
              `auth.manage_org` the Save button surfaces a server-side
              403 with a readable message — same defense-in-depth shape
              the loyalty + branding settings pages use. */}
          <Route
            path="settings/tip-pool-policy"
            element={
              <PermissionGate permission="pos.view_tip_reports">
                <TipPoolPolicySettings />
              </PermissionGate>
            }
          />

          {/* Phase 37.3 — Menu upsells authoring. Gate on
              `pos.manage_upsells` (37.1 seed; backfilled onto every role
              with `pos.manage_products`). The page itself surfaces server
              error codes (SELF_LOOP, RECOMMENDATION_INACTIVE, etc.) and
              pins them to the offending field so authors don't have to
              parse strings. */}
          {/* Phase 59.6 — menus (Drinks / Lunch / Dinner). Rides
              pos.manage_products: a menu IS catalog organisation, the
              same permission that gates the categories it holds. Not
              feature-gated — every org has at least the default menu the
              0164 backfill created, so there is nothing to turn on. */}
          <Route
            path="settings/menus"
            element={
              <PermissionGate permission="pos.manage_products">
                <MenusPage />
              </PermissionGate>
            }
          />
          {/* Phase 64 — the online menu. Catalog authority (the API's
              writes need pos.manage_products) inside the storefront's own
              suite gate, like /settings/storefront. Its own route rather
              than a Storefront tab: it is a long catalog editor, not a
              settings card. */}
          <Route
            path="settings/online-menu"
            element={
              <PermissionGate permission="pos.manage_products">
                <FeatureGate featureId="pos.online_ordering">
                  <OnlineMenuPage />
                </FeatureGate>
              </PermissionGate>
            }
          />
          <Route
            path="settings/upsells"
            element={
              <FeatureGate featureId="pos.menu_upsells"><PermissionGate permission="pos.manage_upsells">
                <UpsellsPage />
              </PermissionGate></FeatureGate>
            }
          />
          {/* Phase 52.5 — time-based price rules (happy hour / dayparts).
              Rides pos.manage_products: a price rule IS catalog pricing.
              Feature-gated on pos.price_rules (Copilot 2026-08-19) so
              orgs with the flag off get the standard disabled notice. */}
          <Route
            path="settings/price-rules"
            element={
              <FeatureGate featureId="pos.price_rules">
                <PermissionGate permission="pos.manage_products">
                  <PriceRulesPage />
                </PermissionGate>
              </FeatureGate>
            }
          />

          {/* Phase 45.1b — enterprise SSO (SAML/OIDC) configuration.
              Gated on `auth.configure_sso`; the page renders its own
              permission notice too (defense in depth). */}
          <Route
            path="settings/security/sso"
            element={
              <PermissionGate permission="auth.configure_sso">
                {/* Phase 64 — the Enterprise `sso` suite (sign-in through an existing connection is never gated). */}
                <FeatureGate featureId="auth.sso">
                  <SsoSettingsPage />
                </FeatureGate>
              </PermissionGate>
            }
          />

          {/* Phase 41.1d — outbox DLQ + replay operator surface. */}
          <Route path="settings/system-health" element={<PlatformGuard />}>
            <Route index element={<Navigate to="/platform/system-health" replace />} />
          </Route>

          {/* Phase 36.4 — billing settings. The page composes the merchant
              consumer surface (live subscription card + invoice-history
              placeholder + change-plan modal) above the operator-mint
              card. Gate widens to `billing.view` so any merchant role
              with subscription visibility lands; the sales-driven sign-up
              card inside the page stays gated on
              `billing.manage_signup_tokens` (sales-tier authority). */}
          <Route
            path="settings/billing"
            element={
              <PermissionGate permission="billing.view">
                <BillingPage />
              </PermissionGate>
            }
          />

          {/* Phase 36.2c — billing-email deep link. No PermissionGate
              redirect here: the page renders its own permission notice so
              a non-owner clicking an email link sees why nothing opened
              instead of silently landing on the home page. */}
          <Route path="billing/portal" element={<PermissionGate permission="billing.manage"><BillingPortalRedirect /></PermissionGate>} />

          {/* Phase 27a — platform admin surface. Cross-org tools for
              onboarding new merchants + resetting forgotten passwords +
              managing org status (active / pending / suspended /
              archived). Gated on the `platform_admin` role at the
              router level so non-admins see no trace of the surface.
              The server enforces the same restriction independently via
              PlatformAdminGuard. */}
          <Route element={<PlatformGuard />}>
            <Route path="platform" element={<PlatformDashboard />} />
            <Route path="platform/system-health" element={<SystemHealthPage />} />
            <Route path="platform/orgs" element={<OrgsList />} />
            <Route path="platform/orgs/new" element={<CreateOrgPage />} />
            <Route path="platform/orgs/:id" element={<OrgDetail />} />
            {/* Phase 45.4d — franchise / multi-org hierarchy admin. */}
            <Route path="platform/org-groups" element={<OrgGroupsPage />} />
            {/* Phase 66.16 (V15) — every pilot, soonest readout first. */}
            <Route path="platform/pilots" element={<PlatformPilotsPage />} />
            {/* Phase 66.20 (V19) — contribution per paying merchant, by month. */}
            <Route path="platform/contribution" element={<PlatformContributionPage />} />
            {/* Phase 68.5 — texting registrations that wait on Commerce OS. */}
            <Route path="platform/texting" element={<PlatformTextingPage />} />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
    </Suspense>
  );
}
